import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, FileText, Ship, MapPin, Package, Calendar, DollarSign, MessageSquare, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const QuoteResponse = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const quoteId = searchParams.get('quoteId');
    const action = searchParams.get('action');
    
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        if (quoteId) {
            fetchQuoteDetails();
        }
    }, [quoteId]);
    
    const fetchQuoteDetails = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/quote/details/${quoteId}`);
            if (res.data.success) {
                setQuote(res.data.quote);
                if (res.data.quote.status !== 'pending') {
                    setSubmitted(true);
                }
            }
        } catch (err) {
            console.error('Error fetching quote:', err);
            setError('Quote not found or has expired');
        }
        setLoading(false);
    };
    
    const handleSubmit = async () => {
        if (!remarks.trim()) {
            alert('Please add your remarks before submitting');
            return;
        }
        
        setSubmitting(true);
        try {
            const res = await axios.post(`${BACKEND_URL}/api/quote/respond`, {
                quoteId,
                action,
                remarks
            });
            
            if (res.data.success) {
                setSubmitted(true);
            }
        } catch (err) {
            console.error('Error submitting response:', err);
            setError('Failed to submit response. Please try again.');
        }
        setSubmitting(false);
    };
    
    const isAccept = action === 'accept';
    
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
                    <p className="text-slate-400">Loading quote details...</p>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full text-center border border-slate-700">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Quote Not Found</h1>
                    <p className="text-slate-400 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                        Go to Homepage
                    </button>
                </div>
            </div>
        );
    }
    
    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full text-center border border-slate-700">
                    {quote?.status === 'accepted' || (isAccept && submitted) ? (
                        <>
                            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle className="w-10 h-10 text-emerald-500" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2">Quote Accepted!</h1>
                            <p className="text-slate-400 mb-6">
                                Thank you for accepting quote <span className="text-teal-400 font-semibold">{quoteId}</span>. 
                                Our team will contact you shortly to proceed with the booking.
                            </p>
                        </>
                    ) : (
                        <>
                            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <XCircle className="w-10 h-10 text-red-500" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2">Quote Declined</h1>
                            <p className="text-slate-400 mb-6">
                                You have declined quote <span className="text-teal-400 font-semibold">{quoteId}</span>. 
                                We appreciate your feedback and hope to serve you in the future.
                            </p>
                        </>
                    )}
                    {quote?.responseRemarks && (
                        <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
                            <p className="text-xs text-slate-400 mb-1">Your Remarks:</p>
                            <p className="text-slate-300 text-sm">{quote.responseRemarks}</p>
                        </div>
                    )}
                    <button
                        onClick={() => window.close()}
                        className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                    >
                        Close Window
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">
                        shipez<span className="text-teal-400">.ai</span>
                    </h1>
                    <p className="text-slate-400">AI-Powered Freight Solutions</p>
                </div>
                
                {/* Action Card */}
                <div className={`bg-slate-800 rounded-2xl overflow-hidden border-2 ${isAccept ? 'border-emerald-500/50' : 'border-red-500/50'}`}>
                    {/* Action Header */}
                    <div className={`p-6 ${isAccept ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                        <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isAccept ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                                {isAccept ? (
                                    <CheckCircle className="w-7 h-7 text-emerald-500" />
                                ) : (
                                    <XCircle className="w-7 h-7 text-red-500" />
                                )}
                            </div>
                            <div>
                                <h2 className={`text-xl font-bold ${isAccept ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {isAccept ? 'Accept Quotation' : 'Decline Quotation'}
                                </h2>
                                <p className="text-slate-400 text-sm">Quote #{quoteId}</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Quote Summary */}
                    {quote && (
                        <div className="p-6 border-b border-slate-700">
                            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Quote Summary</h3>
                            
                            {/* Route */}
                            <div className="bg-slate-700/30 rounded-xl p-4 mb-4">
                                <div className="flex items-center justify-between">
                                    <div className="text-center">
                                        <MapPin className="w-5 h-5 text-teal-500 mx-auto mb-1" />
                                        <p className="text-white font-semibold">{quote.origin}</p>
                                        <p className="text-xs text-slate-400">Origin</p>
                                    </div>
                                    <div className="flex-1 px-4">
                                        <div className="border-t-2 border-dashed border-slate-600 relative">
                                            <Ship className="w-5 h-5 text-teal-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 px-1" />
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <MapPin className="w-5 h-5 text-teal-500 mx-auto mb-1" />
                                        <p className="text-white font-semibold">{quote.destination}</p>
                                        <p className="text-xs text-slate-400">Destination</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="bg-slate-700/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                                        <Ship className="w-3 h-3" /> Carrier
                                    </div>
                                    <p className="text-white font-medium">{quote.carrier}</p>
                                </div>
                                <div className="bg-slate-700/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                                        <Package className="w-3 h-3" /> Container
                                    </div>
                                    <p className="text-white font-medium">{quote.containerQty}x {quote.containerType}</p>
                                </div>
                                <div className="bg-slate-700/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                                        <Calendar className="w-3 h-3" /> Valid Until
                                    </div>
                                    <p className="text-white font-medium">{quote.validUntil}</p>
                                </div>
                                <div className="bg-slate-700/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                                        <DollarSign className="w-3 h-3" /> Total Amount
                                    </div>
                                    <p className="text-emerald-400 font-bold text-lg">${quote.totalAmount?.toLocaleString()} {quote.currency}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Remarks Section */}
                    <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <MessageSquare className={`w-5 h-5 ${isAccept ? 'text-emerald-500' : 'text-red-500'}`} />
                            <h3 className="text-white font-semibold">Add Your Remarks</h3>
                            <span className="text-red-400 text-sm">*Required</span>
                        </div>
                        <p className="text-slate-400 text-sm mb-4">
                            {isAccept 
                                ? 'Please provide any additional instructions or comments for proceeding with this booking.'
                                : 'Please let us know why you are declining this quote so we can serve you better.'
                            }
                        </p>
                        <textarea
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder={isAccept 
                                ? "e.g., Please proceed with booking. Preferred vessel sailing date: next week..."
                                : "e.g., Price is higher than expected. Looking for rates under $2000..."
                            }
                            className="w-full h-32 bg-slate-700/50 border border-slate-600 rounded-xl p-4 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                        />
                        
                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => window.close()}
                                className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting || !remarks.trim()}
                                className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                                    isAccept 
                                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-emerald-800 disabled:cursor-not-allowed'
                                        : 'bg-red-600 hover:bg-red-700 text-white disabled:bg-red-800 disabled:cursor-not-allowed'
                                }`}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        {isAccept ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                                        {isAccept ? 'Confirm Accept' : 'Confirm Decline'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Footer */}
                <div className="text-center mt-6 text-slate-500 text-sm">
                    <p>© 2026 Shipez.ai - AI-Powered Freight Solutions</p>
                </div>
            </div>
        </div>
    );
};

export default QuoteResponse;
